import { FC } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import classNames from "classnames";

export interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  animationDuration?: number;
  height?: string;
}

export const ProgressBar: FC<ProgressBarProps> = ({ 
  currentStep, 
  totalSteps = 3,
  animationDuration = 1.5,
  height = "h-4"
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [previousPercentage, setPreviousPercentage] = useState(0);
  const percentage = Math.min((currentStep / totalSteps) * 100, 100);
  const progressRef = useRef(useSpring(0, {
    stiffness: 100,
    damping: 30
  }));
  
  const progress = progressRef.current;
  const width = useTransform(progress, [0, 100], ["0%", "100%"]);

  useEffect(() => {
    // Store the previous percentage before updating
    setPreviousPercentage(progress.get());
    
    // Animate from current value to new value
    const timer = setTimeout(() => {
      progress.set(percentage);
    }, 100);

    // Animate the counter from previous value to new value
    const duration = animationDuration * 1000;
    const steps = 60;
    const startValue = Math.floor(previousPercentage);
    const endValue = Math.floor(percentage);
    const difference = endValue - startValue;
    const increment = difference / steps;
    let currentStep = 0;

    const counterInterval = setInterval(() => {
      if (currentStep < steps) {
        setDisplayValue(Math.floor(startValue + (increment * currentStep)));
        currentStep++;
      } else {
        setDisplayValue(Math.floor(percentage));
        clearInterval(counterInterval);
      }
    }, duration / steps);

    return () => {
      clearTimeout(timer);
      clearInterval(counterInterval);
    };
  }, [percentage, progress, animationDuration, previousPercentage]);

  return (
    <>
      <div className="rounded-lg mb-8 space-y-6">
        {/* Progress bar with percentage inside */}
        <div className="w-full">
          <div
            className={classNames(
              "relative bg-gray-200 rounded-full overflow-hidden",
              height
            )}
          >
            <motion.div
              className="h-full bg-primary rounded-full relative flex items-center justify-end pr-3"
              style={{ width }}
            ></motion.div>
          </div>
        </div>
      </div>
    </>
  );
}; 